import { KeyValueCustomMultipleSelect, Paragraph, Pill } from "@smartsuite/react-ui/lib";
import { useMemo, useRef } from "react";
import * as yup from "yup";
import { FormFieldControl } from "../../../../components/FormFieldControl/FormFieldControl";
import { useFormContext } from "../../../../hooks/useFormContext";
import { useFormField } from "../../../../hooks/useFormField";
import { isMobilePhone } from "../../../../utils/devices";
import { $t } from "../../../../utils/intl";
import type { FormFieldProps } from "../../../types";
import { validateWithYup } from "../../../validator";
import { validatorText } from "../../../validator.text";
import { fieldsText } from "../../fields.text";
import type { MultipleSelectFieldValue } from "../multiple-select.config";

import "./MultipleSelectFieldControl.sass";
import type { FieldChoice } from "@smartsuite/types";
import {
  OTHER_OPTION_VALUE,
  OtherOptionField,
} from "../../../../components/OtherOptionField/OtherOptionField";
import { useResetOtherOptionField } from "../../../../components/OtherOptionField/OtherOptionField.hook";
import { translateOtherOption } from "../../../../components/OtherOptionField/OtherOptionField.utils";
import { useNormalizedMultipleValue } from "../../../../hooks/useNormalizedMultipleValue";
import { ChoiceSelect } from "../../components/ChoiceSelect/ChoiceSelect";

export type MultipleSelectFieldControlProps = FormFieldProps;

export const MultipleSelectFieldControl: React.FunctionComponent<
  MultipleSelectFieldControlProps
> = ({ field, formItem, name, label, required, caption, readOnly }) => {
  const requiredText = $t(validatorText.validationRequired);
  const { config } = useFormContext();
  const isNewDisplayTypesEnabled = config.feature_flags?.form_select_display_types === true;
  const displayType: "dropdown" | "inline" = isNewDisplayTypesEnabled
    ? ((formItem.params?.display_type as "dropdown" | "inline" | undefined) ?? "dropdown")
    : "dropdown";
  const columnCount = (formItem.params?.number_of_columns as 1 | 2 | 3 | undefined) ?? 2;
  const dummyItemRef = useRef<HTMLSpanElement>(null);
  const formField = useFormField<MultipleSelectFieldValue>({
    name,
    validate: validateWithYup(
      yup
        .array()
        .of(yup.string())
        .when({
          is: () => formItem.required,
          then: (lr) => lr.required(requiredText).min(1, requiredText),
        }),
    ),
  });

  // A restored checkpoint, a URL prefill or an internal-form record can hold the scalar shape this
  // field had while it was still single-valued (FORMS-WEB-X1). Rendering it threw `.map is not a
  // function`, and leaving it in Formik makes the yup.array() validator above reject an untouched
  // field on submit (BUG-21363).
  const selectedValues = useNormalizedMultipleValue<string>(formField.value, formField.onChange);

  const isShowOtherOptionField = selectedValues.includes(OTHER_OPTION_VALUE);

  const options = useMemo(() => {
    const baseChoices: FieldChoice[] = field.params?.choices ?? [];

    if (formItem.params?.selections?.enabled ?? false) {
      const selectionChoicesSet = new Set(formItem.params?.selections?.choiceIDs ?? []);
      return baseChoices.filter((choice) => selectionChoicesSet.has(choice.value as string)) ?? [];
    } else {
      return baseChoices;
    }
  }, [field.params?.choices, formItem.params?.selections]);

  const translatedOptions = useMemo(() => {
    return translateOtherOption(options);
  }, [options]);

  const handleOpenChanged = (opened: boolean): void => {
    if (opened && isMobilePhone()) dummyItemRef.current?.scrollIntoView(true);
  };

  useResetOtherOptionField(name, isShowOtherOptionField);

  return (
    <FormFieldControl
      captionAsTooltip={formItem.params.help_text_as_tooltip}
      label={label}
      helpText={formItem.params.helpText}
      caption={caption}
      required={required}
      state={formField.state}
      errorMessage={formField.errorMessage}
      readOnly={readOnly}
    >
      {isNewDisplayTypesEnabled ? (
        <>
          <span ref={dummyItemRef} />
          <ChoiceSelect
            formState={formField.state}
            isMultipleSelect={true}
            columnCount={columnCount}
            type={displayType}
            name={name}
            options={translatedOptions}
            value={selectedValues}
            readOnly={readOnly}
            disabled={readOnly}
            placeholder={$t(fieldsText.multipleSelectPlaceholder)}
            searchPlaceholder={$t(fieldsText.multipleSelectSearchOptionPlaceholder)}
            showFilterGreaterThan={4}
            collision={isMobilePhone() ? "none" : "flipfit"}
            onChange={formField.onChange}
            onChangeFocused={formField.onChangeFocused}
            onOpenedChange={handleOpenChanged}
          />
          {displayType === "dropdown" && isShowOtherOptionField && (
            <OtherOptionField name={`metadata_${name}`} />
          )}
        </>
      ) : (
        <>
          <span ref={dummyItemRef} />
          <KeyValueCustomMultipleSelect<"value", "label", FieldChoice>
            collision={isMobilePhone() ? "none" : "flipfit"}
            inputBackground="white-new"
            className="form-multiple-select-field"
            placeholderArrowStyle="arrow-down-smaller"
            onChange={formField.onChange}
            onChangeFocused={formField.onChangeFocused}
            onOpenedChange={handleOpenChanged}
            options={translatedOptions}
            isReadOnly={readOnly}
            disabled={readOnly}
            placeholder={$t(fieldsText.multipleSelectPlaceholder)}
            renderSelectedOption={(option) => <Pill text={option.label} />}
            renderOption={(option) => (
              <div className="multiple-select-field-control-option">
                <Paragraph tag="div" weight="medium" wordBreak="break-word" color="currentColor">
                  {option.label}
                </Paragraph>

                {option.value_help_text && (
                  <Paragraph size="xs" color="currentColor">
                    {option.value_help_text}
                  </Paragraph>
                )}
              </div>
            )}
            searchPlaceholder={$t(fieldsText.multipleSelectSearchOptionPlaceholder)}
            showFilterGreaterThan={4}
            state={formField.state}
            value={selectedValues}
            selectListItemsClass="multiple-select-field-control-list"
          />
          {isShowOtherOptionField && <OtherOptionField name={`metadata_${name}`} />}
        </>
      )}
    </FormFieldControl>
  );
};
